import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

type NavOption = {
  path: string;
  name: string;
};

type ResponsiveNavTabsProps = {
  options: NavOption[];
  getFullPath: (option: NavOption) => string;
  isTabActive: (path: string) => boolean;
  isVerySmallScreen?: boolean;
  isMediumScreen?: boolean;
  menuGap?: number;
};

const MORE_BUTTON_WIDTH = 38;

const ResponsiveNavTabs: React.FC<ResponsiveNavTabsProps> = ({
  options,
  getFullPath,
  isTabActive,
  isVerySmallScreen = false,
  isMediumScreen = false,
  menuGap = 8,
}) => {
  const location = useLocation();

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const [visibleOptions, setVisibleOptions] = useState<NavOption[]>(options);

  const [hiddenOptions, setHiddenOptions] = useState<NavOption[]>([]);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);

  const moreOpen = Boolean(moreAnchorEl);

  const getTabStyle = useCallback(
    (active: boolean): React.CSSProperties => ({
      textDecoration: "none",

      padding: isVerySmallScreen
        ? "4px 8px"
        : isMediumScreen
          ? "6px 10px"
          : "8px 14px",

      color: active ? "#FFF" : "#7D949D",

      fontWeight: active ? "bold" : 400,

      backgroundColor: active ? "#2F4853" : "transparent",

      borderRadius: "8px",

      whiteSpace: "nowrap",

      minWidth: "max-content",

      flexShrink: 0,

      fontSize: isVerySmallScreen
        ? "0.85rem"
        : isMediumScreen
          ? "0.95rem"
          : "1rem",
    }),
    [isVerySmallScreen, isMediumScreen],
  );

  const isOptionActive = useCallback(
    (option: NavOption) => {
      const fullPath = getFullPath(option);
      if (isTabActive(option.path)) return true;
      if (location.pathname === fullPath) return true;
      if (fullPath !== "/" && location.pathname.startsWith(fullPath + "/")) {
        return true;
      }
      return false;
    },
    [getFullPath, isTabActive, location.pathname],
  );

  const getActiveOption = useCallback(() => {
    return options.find((option) => isOptionActive(option));
  }, [options, isOptionActive]);

  const calculateTabs = useCallback(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;

    if (!container || !measureContainer) {
      return;
    }

    const availableWidth = container.clientWidth;

    const measurementElements = Array.from(
      measureContainer.children,
    ) as HTMLElement[];

    const activeOption = getActiveOption();
    const activeIndex = activeOption
      ? options.findIndex((option) => option.path === activeOption.path)
      : -1;

    if (!measurementElements.length || options.length === 0) {
      if (activeOption) {
        setVisibleOptions([activeOption]);
        setHiddenOptions(options.filter((o) => o.path !== activeOption.path));
      } else {
        setVisibleOptions(options);
        setHiddenOptions([]);
      }
      return;
    }

    const widths = options.map(
      (_, index) =>
        measurementElements[index]?.getBoundingClientRect().width || 0,
    );

    const totalAllWidth =
      widths.reduce((sum, w) => sum + w, 0) +
      Math.max(0, options.length - 1) * menuGap;

    if (totalAllWidth <= availableWidth) {
      setVisibleOptions(options);
      setHiddenOptions([]);
      return;
    }

    const moreBtnSpace = MORE_BUTTON_WIDTH + menuGap;

    let visibleIndexes: number[] = [];
    let usedWidth = 0;

    for (let i = 0; i < options.length; i++) {
      const tabWidth = widths[i];
      const gap = visibleIndexes.length > 0 ? menuGap : 0;

      const nextUsedWidth = usedWidth + gap + tabWidth;

      if (nextUsedWidth + moreBtnSpace <= availableWidth) {
        visibleIndexes.push(i);
        usedWidth = nextUsedWidth;
      } else {
        break;
      }
    }

    if (visibleIndexes.length === 0 && options.length > 0) {
      visibleIndexes.push(0);
    }

    if (activeIndex !== -1 && !visibleIndexes.includes(activeIndex)) {
      // Replace the last visible tab
      visibleIndexes[visibleIndexes.length - 1] = activeIndex;
    }

    visibleIndexes = [...new Set(visibleIndexes)].sort((a, b) => a - b);

    const finalVisible = visibleIndexes.map((index) => options[index]);

    const finalHidden = options.filter(
      (_, index) => !visibleIndexes.includes(index),
    );

    setVisibleOptions(finalVisible);
    setHiddenOptions(finalHidden);
  }, [options, getActiveOption, menuGap]);

  useLayoutEffect(() => {
    calculateTabs();
  }, [calculateTabs]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const observer = new ResizeObserver(() => {
      calculateTabs();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [calculateTabs]);

  useEffect(() => {
    window.addEventListener("resize", calculateTabs);

    return () => {
      window.removeEventListener("resize", calculateTabs);
    };
  }, [calculateTabs]);

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: `${menuGap}px`,
          minWidth: 0,
          flex: 1,
          overflow: "hidden",
        }}
      >
        {visibleOptions.map((option) => {
          const fullPath = getFullPath(option);

          return (
            <NavLink
              key={option.path}
              to={fullPath}
              style={({ isActive }) => {
                const active = isActive || isOptionActive(option);

                return getTabStyle(active);
              }}
            >
              {option.name}
            </NavLink>
          );
        })}

        {hiddenOptions.length > 0 && (
          <IconButton
            onClick={handleMoreClick}
            size="small"
            aria-label="More navigation options"
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              flexShrink: 0,
              color: "#FFF",
              backgroundColor: "#2F4853",
              borderRadius: "7px",
              "&:hover": {
                backgroundColor: "#3A5661",
              },
            }}
          >
            <KeyboardDoubleArrowRightIcon
              sx={{
                fontSize: 20,
              }}
            />
          </IconButton>
        )}
      </Box>

      <Box
        ref={measureRef}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,

          visibility: "hidden",

          pointerEvents: "none",

          display: "flex",

          whiteSpace: "nowrap",
        }}
      >
        {options.map((option) => (
          <Box
            key={option.path}
            sx={{
              ...getTabStyle(false),

              position: "relative",
            }}
          >
            {option.name}
          </Box>
        ))}
      </Box>

      <Menu
        anchorEl={moreAnchorEl}
        open={moreOpen}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 160,
              borderRadius: "8px",
            },
          },
        }}
      >
        {hiddenOptions.map((option) => {
          const fullPath = getFullPath(option);
          const active = isOptionActive(option);

          return (
            <MenuItem
              key={option.path}
              component={NavLink}
              to={fullPath}
              onClick={handleMoreClose}
              sx={{
                fontSize: "13px",

                fontWeight: active ? 700 : 400,

                color: active ? "#0682B5" : "#363D44",

                backgroundColor: active ? "#F0F9FC" : "transparent",

                "&:hover": {
                  backgroundColor: "#F5F7F8",
                },
              }}
            >
              {option.name}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ResponsiveNavTabs;
